import React, { ReactElement, Fragment, useState } from 'react';
import { ObjectHistoryLog } from '@/types';
import { UserInfoRowLazy } from '@/components/UserInfoRow';
import { Trans } from '@lingui/react/macro';
import { get } from 'lodash-es';
import Tags from '@/components/Tags';
import { DateDisplay } from '@/components/localization';
import HistoryLogComparison from './HistoryLogComparison';
import { ResidentialUnitTooltip } from '../data-tooltips';
import { ProfileIcon } from '@/components/Icons';
import { BadgeList, BorderBadge, TextView, Modal, ModalBody, ModalFooter, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import styles from './HistoryLogTableInfoRow.module.scss';
import { AcceptedTermsTooltip } from './AcceptedTermsTooltip';

interface HistoryLogTableInfoRowProps {
    row: ObjectHistoryLog;
}

const HistoryLogTableInfoRow = ({ row }: HistoryLogTableInfoRowProps) => {
    const [showMoreDocs, setShowMoreDocs] = useState(false);
    const [showMoreUnits, setShowMoreUnits] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showProjectBulkModal, setShowProjectBulkModal] = useState(false);

    if (!row.parametersJson) return null;
    let outArray: ReactElement[] = [];

    for (const [key, value] of Object.entries(row.parametersJson)) {
        if (!value) continue;
        if (key === 'comparison') {
            outArray.push(
                <Fragment key={key}>
                    <TextView numberOfLines={1}>
                        <HistoryLogComparison data={value} operationKey={row.operationKey} />
                    </TextView>
                </Fragment>
            );
        }

        if (key === 'userId') {
            outArray.push(<UserInfoRowLazy key={key + value} userId={value} />);
        }
        if (key === 'participantUserId') {
            outArray.push(
                <UserInfoRowLazy
                    userId={value}
                    infoText={
                        <>
                            <Trans>Participant:</Trans>{' '}
                        </>
                    }
                />
            );
        }
        if (key === 'profileId') {
            outArray.push(<ProfileIcon key={key + value} sizeInPx={30} profileId={value} />);
        }

        if (key === 'tags') {
            outArray.push(
                <Fragment key="tags">
                    <Trans>Tags:</Trans>
                    <Tags size="small" tags={row.parametersJson['tags']} />
                </Fragment>
            );
        }
        if (key === 'reason') {
            outArray.push(
                <Fragment key="reason">
                    <Trans>Reason:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'message') {
            outArray.push(
                <Fragment key="message">
                    <Trans>Message:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'email') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Email:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'phoneNumber') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Phone number:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'title') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Title:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'description') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Description:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'name') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Name:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'label') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Label:</Trans> {value}
                </Fragment>
            );
        }
        // if (key === 'units') {
        //     outArray.push(
        //         <BadgeList key={key}>
        //             {value.map((ru) => (
        //                 <ResidentialUnitTooltip
        //                     key={ru.id}
        //                     residentialUnitId={ru.id}
        //                     linkTab="options"
        //                     includeQuestionMark={false}>
        //                     <BorderBadge>{ru.name}</BorderBadge>
        //                 </ResidentialUnitTooltip>
        //             ))}
        //         </BadgeList>
        //     );
        // }
        if (key === 'subject') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Subject:</Trans> {value}
                </Fragment>
            );
        }
        if (key === 'subjects') {
            outArray.push(
                <Fragment key={key + value}>
                    <Trans>Subject:</Trans> {value.join(', ')}
                </Fragment>
            );
        }
    }
    if (row.objectType === 'ResidentialUnit') {
        if (row.operationKey === 'DeadlineUpdated') {
            return (
                <Fragment key={row.id}>
                    <Trans>New date:</Trans> <DateDisplay value={get(row.parametersJson, 'date', '')} />
                </Fragment>
            );
        }

        if (row.operationKey === 'TermsAccepted') {
            return (
                <Fragment key="terms-accepted">
                    <AcceptedTermsTooltip parametersJson={row.parametersJson} />
                </Fragment>
            );
        }
        // Handle bulk document operations that are logged as ResidentialUnit updates
        if (
            row.operationKey === 'Updated' &&
            (row.parametersJson['operationType'] === 'DocumentsBulkApproved' ||
                row.parametersJson['operationType'] === 'DocumentsBulkRejected')
        ) {
            const documents = get(row.parametersJson, 'documents', []) as Array<{ name: string; documentId: string }>;
            const operationType = get(row.parametersJson, 'operationType', '') as string;
            const documentType = get(row.parametersJson, 'documentType', '') as string;
            const count = get(row.parametersJson, 'count', documents.length) as number;

            outArray = [];

            // Show summary based on operation and document type
            const summaryText =
                operationType === 'DocumentsBulkApproved' ? (
                    documentType === 'ProjectDocument' ? (
                        <Trans>{count} project documents approved</Trans>
                    ) : (
                        <Trans>{count} documents approved</Trans>
                    )
                ) : documentType === 'ProjectDocument' ? (
                    <Trans>{count} project documents rejected</Trans>
                ) : (
                    <Trans>{count} documents rejected</Trans>
                );

            outArray.push(
                <Fragment key="bulk-documents">
                    <div>
                        <strong>{summaryText}</strong>
                    </div>

                    {showMoreDocs && (
                        <>
                            <div className={styles.list}>
                                <Trans>Documents:</Trans> {documents.map((d) => d.name).join(', ')}
                            </div>
                            {row.parametersJson['reason'] && (
                                <div className={styles.section}>
                                    <Trans>Reason:</Trans> {row.parametersJson['reason']}
                                </div>
                            )}
                            {row.parametersJson['message'] && (
                                <div className={styles.section}>
                                    <Trans>Message:</Trans> {row.parametersJson['message']}
                                </div>
                            )}
                        </>
                    )}

                    <div className={styles.showMoreSection}>
                        <Button
                            color="link"
                            size="small"
                            onClick={() => setShowMoreDocs(!showMoreDocs)}
                            icon={showMoreDocs ? faChevronUp : faChevronDown}>
                            {showMoreDocs ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
                        </Button>
                    </div>
                </Fragment>
            );

            return <>{outArray.map((x) => x)}</>;
        }
    }

    if (row.objectType === 'OptionPreset' && row.operationKey === 'ItemsUpdated') {
        const changes = get(row.parametersJson, 'Changes', []) as Array<{
            ProductName: string;
            OptionGroupName: string | null;
            Action: string;
        }>;
        if (changes.length > 0) {
            outArray = [];
            outArray.push(
                <Fragment key="items-updated">
                    <BadgeList>
                        {changes.map((change, index) => (
                            <BorderBadge key={index}>
                                {change.OptionGroupName ? `${change.OptionGroupName}: ` : ''}
                                {change.ProductName} ({change.Action})
                            </BorderBadge>
                        ))}
                    </BadgeList>
                </Fragment>
            );
            return <>{outArray.map((x) => x)}</>;
        }
    }

    if (
        row.objectType === 'ProjectCatalogLinkGroup' &&
        (row.operationKey === 'ProductStyleAdded' ||
            row.operationKey === 'ProductStyleRemoved' ||
            row.operationKey === 'ProductStyleChanged')
    ) {
        const styleName = (get(row.parametersJson, 'style') ?? get(row.parametersJson, 'Style') ?? '') as string;
        const changes = (get(row.parametersJson, 'changes') ?? get(row.parametersJson, 'Changes') ?? []) as Array<{
            productName?: string;
            ProductName?: string;
            action?: string;
            Action?: string;
        }>;

        const productNames = changes
            .map((x) => x.productName ?? x.ProductName)
            .filter((x): x is string => !!x);

        if (styleName || productNames.length > 0) {
            outArray = [];
            outArray.push(
                <Fragment key="product-style-updated">
                    {styleName && (
                        <>
                            <Trans>Style:</Trans> {styleName}
                        </>
                    )}
                    {styleName && productNames.length > 0 && ', '}
                    {productNames.length > 0 && (
                        <>
                            <Trans>Product:</Trans> {productNames.join(', ')}
                        </>
                    )}
                </Fragment>
            );
            return <>{outArray.map((x) => x)}</>;
        }
    }

    if (row.objectType === 'ChangeRequest') {
        if (row.operationKey === 'ParticipantReadyChanged') {
            outArray.push(
                <Fragment key="CR-ParticipantsReady">
                    {row.parametersJson['ready'] ? <Trans>Ready</Trans> : <Trans>Not ready</Trans>}
                </Fragment>
            );
        }

        if (row.operationKey === 'SetOptionOverrides') {
            outArray.push(
                <Fragment key="CR-SetOptionOverrides">
                    <Trans>Overridden options:</Trans>{' '}
                    {(get(row.parametersJson, 'buyerOptionGroupNames', []) as string[]).join(' ,')}
                </Fragment>
            );
        }
    }

    if (row.objectType === 'Project') {
        if (row.operationKey === 'DocumentsBulkApproved' || row.operationKey === 'DocumentsBulkRejected') {
            const documents = get(row.parametersJson, 'documents', []) as Array<{ name: string; documentId: string }>;
            const units = get(row.parametersJson, 'units', []) as Array<{ id: number; name: string }>;
            const objectType = get(row.parametersJson, 'objectType', null) as string | null;
            const count = get(row.parametersJson, 'count', documents.length) as number;
            const residentialUnitId = get(row.parametersJson, 'residentialUnitId', null) as number | null;
            const residentialUnitName = get(row.parametersJson, 'residentialUnitName', null) as string | null;

            outArray = [];

            // Show summary based on whether it's unit-specific or project-level
            const summaryText =
                objectType === 'ResidentialUnitDocument' ? (
                    row.operationKey === 'DocumentsBulkApproved' ? (
                        <Trans>{count} unit documents approved</Trans>
                    ) : (
                        <Trans>{count} unit documents rejected</Trans>
                    )
                ) : objectType === 'ProjectDocument' ? (
                    row.operationKey === 'DocumentsBulkApproved' ? (
                        <Trans>{count} project documents approved</Trans>
                    ) : (
                        <Trans>{count} project documents rejected</Trans>
                    )
                ) : row.operationKey === 'DocumentsBulkApproved' ? (
                    <Trans>{count} documents approved</Trans>
                ) : (
                    <Trans>{count} documents rejected</Trans>
                );

            outArray.push(
                <Fragment key="bulk-documents">
                    <div>
                        <strong>{summaryText}</strong>
                    </div>

                    <Button color="link" size="small" onClick={() => setShowProjectBulkModal(true)}>
                        <Trans>Show details</Trans>
                    </Button>

                    <Modal
                        isOpen={showProjectBulkModal}
                        toggle={() => setShowProjectBulkModal(false)}
                        size="lg"
                        className={styles.modalContainer}>
                        <ModalHeader>
                            {row.operationKey === 'DocumentsBulkApproved' ? (
                                <Trans>Bulk Documents Approved</Trans>
                            ) : (
                                <Trans>Bulk Documents Rejected</Trans>
                            )}
                        </ModalHeader>
                        <ModalBody>
                            <div>
                                <strong>
                                    <Trans>Documents ({documents.length}):</Trans>
                                </strong>
                                <ul className={styles.list}>
                                    {documents.map((doc, index) => (
                                        <li key={doc.documentId || index}>{doc.name}</li>
                                    ))}
                                </ul>
                            </div>
                            {units && units.length > 0 && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Affected units ({units.length}):</Trans>
                                    </strong>
                                    <div className={styles.list}>
                                        <BadgeList>
                                            {units.map((ru) => (
                                                <ResidentialUnitTooltip
                                                    key={ru.id}
                                                    residentialUnitId={ru.id}
                                                    linkTab="documents"
                                                    includeQuestionMark={false}>
                                                    <BorderBadge>{ru.name}</BorderBadge>
                                                </ResidentialUnitTooltip>
                                            ))}
                                        </BadgeList>
                                    </div>
                                </div>
                            )}
                            {residentialUnitId && residentialUnitName && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Unit:</Trans>
                                    </strong>
                                    <div className={styles.list}>
                                        <ResidentialUnitTooltip
                                            residentialUnitId={residentialUnitId}
                                            linkTab="documents"
                                            includeQuestionMark={false}>
                                            <BorderBadge>{residentialUnitName}</BorderBadge>
                                        </ResidentialUnitTooltip>
                                    </div>
                                </div>
                            )}
                            {row.parametersJson['reason'] && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Reason:</Trans>
                                    </strong>
                                    <p className={styles.text}>{row.parametersJson['reason']}</p>
                                </div>
                            )}
                            {row.parametersJson['message'] && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Message:</Trans>
                                    </strong>
                                    <p className={styles.text}>{row.parametersJson['message']}</p>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="primary" onClick={() => setShowProjectBulkModal(false)}>
                                <Trans>Close</Trans>
                            </Button>
                        </ModalFooter>
                    </Modal>
                </Fragment>
            );
        }

        if (row.operationKey === 'ProjectOptionTermsUpdated') {
            return (
                <Fragment key="project-option-terms-updated">
                    <TextView numberOfLines={1}>
                        <HistoryLogComparison data={row.parametersJson['comparison']} operationKey={row.operationKey} />
                    </TextView>
                </Fragment>
            );
        }

        if (row.operationKey === 'ProjectTermsAndConditionsUpdated') {
            return (
                <Fragment key="project-option-terms-updated">
                    <TextView numberOfLines={1}>
                        <HistoryLogComparison data={row.parametersJson['comparison']} operationKey={row.operationKey} />
                    </TextView>
                </Fragment>
            );
        }
    }

    // Handle bulk document operations
    if (
        (row.objectType === 'ProjectDocument' || row.objectType === 'ResidentialUnitDocument') &&
        row.parametersJson['isBulkOperation']
    ) {
        const documents = get(row.parametersJson, 'documents', []) as Array<{ name: string; documentId: string }>;

        if (documents.length > 0) {
            outArray = [];

            outArray.push(
                <Fragment key="bulk-unit-documents">
                    <Button color="link" size="small" onClick={() => setShowBulkModal(true)}>
                        <Trans>Show details</Trans>
                    </Button>

                    <Modal isOpen={showBulkModal} toggle={() => setShowBulkModal(false)} size="lg">
                        <ModalHeader>
                            {row.operationKey === 'Approved' ? (
                                <Trans>Bulk Documents Approved</Trans>
                            ) : (
                                <Trans>Bulk Documents Rejected</Trans>
                            )}
                        </ModalHeader>
                        <ModalBody>
                            <div>
                                <strong>
                                    <Trans>Documents ({documents.length}):</Trans>
                                </strong>
                                <ul className={styles.list}>
                                    {documents.map((doc, index) => (
                                        <li key={doc.documentId || index}>{doc.name}</li>
                                    ))}
                                </ul>
                            </div>
                            {row.parametersJson['reason'] && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Reason:</Trans>
                                    </strong>
                                    <p className={styles.text}>{row.parametersJson['reason']}</p>
                                </div>
                            )}
                            {row.parametersJson['message'] && (
                                <div className={styles.section}>
                                    <strong>
                                        <Trans>Message:</Trans>
                                    </strong>
                                    <p className={styles.text}>{row.parametersJson['message']}</p>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="primary" onClick={() => setShowBulkModal(false)}>
                                <Trans>Close</Trans>
                            </Button>
                        </ModalFooter>
                    </Modal>
                </Fragment>
            );

            return <>{outArray.map((x) => x)}</>;
        }
    }

    return <>{outArray.map((x) => x)}</>;
};

export default HistoryLogTableInfoRow;
